/**
 * UserSubscriptionModal Component
 * Modal for viewing and managing user subscription status
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, CreditCard, Clock, RotateCcw, Gift, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Loading, ErrorMessage } from '@/components/common';
import { subscriptionApi, type SubscriptionStatus } from '@/lib/api/subscriptions';

const STATUS_COLORS: Record<SubscriptionStatus, string> = {
  none: 'bg-gray-100 text-gray-800',
  trial: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800',
  expired: 'bg-orange-100 text-orange-800',
  cancelled: 'bg-red-100 text-red-800',
};

const STATUS_LABELS: Record<SubscriptionStatus, string> = {
  none: '未订阅',
  trial: '试用中',
  active: '订阅中',
  expired: '已过期',
  cancelled: '已取消',
};

interface UserSubscriptionModalProps {
  isOpen: boolean;
  userId: string | null;
  userName?: string;
  onClose: () => void;
}

type ActionType = 'reset' | 'grant_trial' | 'grant_subscription' | null;

export const UserSubscriptionModal: React.FC<UserSubscriptionModalProps> = ({
  isOpen,
  userId,
  userName,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const [action, setAction] = useState<ActionType>(null);
  const [reason, setReason] = useState('');
  const [trialDays, setTrialDays] = useState(7);
  const [subscriptionDays, setSubscriptionDays] = useState(30);
  const [productId, setProductId] = useState('monthly');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setAction(null);
      setReason('');
      setTrialDays(7);
      setSubscriptionDays(30);
      setProductId('monthly');
    }
  }, [isOpen]);

  // Fetch user subscription detail
  const { data: userDetail, isLoading, error } = useQuery({
    queryKey: ['user-subscription-detail', userId],
    queryFn: () => subscriptionApi.getUserDetail(userId!),
    enabled: isOpen && !!userId,
  });

  // Mutation for setting subscription
  const setSubscriptionMutation = useMutation({
    mutationFn: async ({ action, reason }: { action: ActionType; reason: string }) => {
      if (!userId || !action) return;

      switch (action) {
        case 'reset':
          return subscriptionApi.resetUserSubscription(userId, reason);
        case 'grant_trial':
          return subscriptionApi.grantTrial(userId, trialDays, reason);
        case 'grant_subscription':
          return subscriptionApi.grantSubscription(userId, productId, subscriptionDays, reason);
      }
    },
    onSuccess: () => {
      // Invalidate all relevant queries
      queryClient.invalidateQueries({ queryKey: ['user-subscription-detail', userId] });
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['user-stats'] });
      setAction(null);
      setReason('');
    },
  });

  const handleSubmit = () => {
    if (!reason.trim()) {
      alert('请填写操作原因');
      return;
    }
    setSubscriptionMutation.mutate({ action, reason });
  };

  if (!isOpen) return null;

  const subscription = userDetail?.subscription;
  const events = userDetail?.events || [];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen px-4">
        {/* Overlay */}
        <div
          className="fixed inset-0 bg-black opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal Content */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 z-10">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <CreditCard className="w-6 h-6 text-blue-600" />
              <div>
                <h3 className="text-lg font-semibold">订阅管理</h3>
                <p className="text-sm text-gray-500">{userName || userId}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          {isLoading && <Loading message="加载中..." />}
          {error && <ErrorMessage error={error instanceof Error ? error.message : '加载失败'} />}

          {userDetail && (
            <div className="space-y-6">
              {/* Current Status */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">基本状态</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">订阅状态</p>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      STATUS_COLORS[subscription?.status || 'none']
                    }`}>
                      {STATUS_LABELS[subscription?.status || 'none']}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">用户等级</p>
                    <p className="text-sm font-medium">{subscription?.role_level || 'free'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">产品</p>
                    <p className="text-sm">{subscription?.product_id || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">自动续订</p>
                    <p className="text-sm">{subscription?.will_renew ? '是' : '否'}</p>
                  </div>
                  {subscription?.trial_end_at && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">试用结束</p>
                      <p className="text-sm">{new Date(subscription.trial_end_at).toLocaleString('zh-CN')}</p>
                    </div>
                  )}
                  {subscription?.subscription_end_at && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">订阅结束</p>
                      <p className="text-sm">{new Date(subscription.subscription_end_at).toLocaleString('zh-CN')}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Info */}
              {subscription && (subscription.payment_channel || subscription.external_transaction_id) && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">支付信息</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">支付渠道</p>
                      <p className="text-sm">{
                        ({ apple_iap: 'Apple IAP', admin: '管理员', wechat_pay: '微信支付', alipay: '支付宝', google_play: 'Google Play', huawei_iap: '华为 IAP' } as Record<string, string>)[subscription.payment_channel || ''] || subscription.payment_channel || '-'
                      }</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">应用商店</p>
                      <p className="text-sm">{subscription.store || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">自动续订状态</p>
                      <p className="text-sm">{subscription.auto_renew_status ? '开启' : '关闭'}</p>
                    </div>
                    {subscription.external_transaction_id && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">交易 ID</p>
                        <p className="text-sm font-mono truncate" title={subscription.external_transaction_id}>
                          {subscription.external_transaction_id}
                        </p>
                      </div>
                    )}
                    {subscription.original_external_transaction_id && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">原始交易 ID</p>
                        <p className="text-sm font-mono truncate" title={subscription.original_external_transaction_id}>
                          {subscription.original_external_transaction_id}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Time Info */}
              {subscription && (subscription.subscription_start_at || subscription.original_purchase_date || subscription.latest_purchase_date) && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">时间信息</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {subscription.subscription_start_at && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">订阅开始</p>
                        <p className="text-sm">{new Date(subscription.subscription_start_at).toLocaleString('zh-CN')}</p>
                      </div>
                    )}
                    {subscription.original_purchase_date && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">首次购买</p>
                        <p className="text-sm">{new Date(subscription.original_purchase_date).toLocaleString('zh-CN')}</p>
                      </div>
                    )}
                    {subscription.latest_purchase_date && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">最近购买</p>
                        <p className="text-sm">{new Date(subscription.latest_purchase_date).toLocaleString('zh-CN')}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              {!action && (
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900">操作</h4>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAction('reset')}
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      重置订阅
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAction('grant_trial')}
                    >
                      <Clock className="w-4 h-4 mr-1" />
                      赠送试用
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAction('grant_subscription')}
                    >
                      <Gift className="w-4 h-4 mr-1" />
                      赠送订阅
                    </Button>
                  </div>
                </div>
              )}

              {/* Action Form */}
              {action && (
                <div className="bg-blue-50 rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-blue-900">
                      {action === 'reset' && '重置订阅'}
                      {action === 'grant_trial' && '赠送试用'}
                      {action === 'grant_subscription' && '赠送订阅'}
                    </h4>
                    <button
                      onClick={() => setAction(null)}
                      className="text-blue-400 hover:text-blue-600"
                    >
                      <XCircle className="w-5 h-5" />
                    </button>
                  </div>

                  {action === 'reset' && (
                    <p className="text-sm text-blue-700">
                      将清除用户的订阅状态，恢复为未订阅。
                    </p>
                  )}

                  {action === 'grant_trial' && (
                    <div>
                      <label className="block text-sm font-medium text-blue-700 mb-1">
                        试用天数
                      </label>
                      <Input
                        type="number"
                        min={1}
                        max={30}
                        value={trialDays}
                        onChange={(e) => setTrialDays(Number(e.target.value))}
                        className="w-32"
                      />
                    </div>
                  )}

                  {action === 'grant_subscription' && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-blue-700 mb-1">
                          产品
                        </label>
                        <select
                          value={productId}
                          onChange={(e) => setProductId(e.target.value)}
                          className="rounded border-gray-300 text-sm"
                        >
                          <option value="monthly">月度会员</option>
                          <option value="yearly">年度会员</option>
                          <option value="lifetime">终身会员</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-blue-700 mb-1">
                          订阅天数
                        </label>
                        <Input
                          type="number"
                          min={1}
                          max={3650}
                          value={subscriptionDays}
                          onChange={(e) => setSubscriptionDays(Number(e.target.value))}
                          className="w-32"
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-1">
                      操作原因 <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="text"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="请输入操作原因（用于审计日志）"
                      className="w-full"
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAction(null)}
                    >
                      取消
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleSubmit}
                      disabled={setSubscriptionMutation.isPending || !reason.trim()}
                    >
                      {setSubscriptionMutation.isPending ? '处理中...' : '确认'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Recent Events */}
              {events.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">最近事件</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {events.slice(0, 10).map((event, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded text-sm"
                      >
                        <div className="flex items-center gap-2">
                          {event.event_source && (
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              event.event_source === 'apple_webhook' ? 'bg-blue-100 text-blue-700' :
                              event.event_source === 'admin' ? 'bg-orange-100 text-orange-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {event.event_source === 'apple_webhook' ? 'Apple' :
                               event.event_source === 'admin' ? '管理员' : '系统'}
                            </span>
                          )}
                          <span className="font-medium">{event.event_type}</span>
                          {event.previous_status && event.new_status && (
                            <span className="text-gray-500">
                              {event.previous_status} → {event.new_status}
                            </span>
                          )}
                          {event.product_id && (
                            <span className="text-xs text-gray-400">({event.product_id})</span>
                          )}
                        </div>
                        <span className="text-xs text-gray-400">
                          {new Date(event.created_at).toLocaleString('zh-CN')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserSubscriptionModal;
